import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { gql, setToken, clearToken } from '@/lib/graphql';

/**
 * Console session — authenticates against the server's `adminLogin` mutation,
 * NOT the tenant `login`. After the lecartte-server admin-user-separation
 * refactor, admins live in their own AdminUserModel collection; the tenant
 * `login` no longer accepts their emails (returns INVALID_CREDENTIALS), and
 * `adminLogin` is the only path that emits tokens carrying `purpose: 'admin'`.
 *
 * Stored on the client:
 *   - console_token         — admin access token (4h TTL)
 *   - console_refresh_token — admin refresh token (7d TTL)
 *   - console_user          — admin profile fetched via `viewer.admin`
 *
 * No `role` field on the admin principal: AdminUser is the role.
 */
interface AuthUser {
  id: string;
  name: string;
  email: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: ({ email, password }: { email: string; password: string }) => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: false,
  login: async () => ({ error: null }),
  logout: async () => {},
});

const ADMIN_LOGIN_MUTATION = `
  mutation AdminLogin($input: AdminLoginInput!) {
    AdminLogin(input: $input) {
      accessToken
      refreshToken
      errors { code message }
    }
  }
`;

const VIEWER_ADMIN_QUERY = `
  query ViewerAdmin {
    viewer {
      admin { id name email }
    }
  }
`;

const ADMIN_LOGOUT_MUTATION = `
  mutation AdminLogout {
    AdminLogout(input: {}) { success }
  }
`;

const REFRESH_TOKEN_KEY = 'console_refresh_token';
const USER_KEY = 'console_user';

function isTokenExpired({ token }: { token: string }): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const token = localStorage.getItem('console_token');
      if (!token) return null;
      if (isTokenExpired({ token })) {
        localStorage.removeItem('console_token');
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        return null;
      }
      const stored = localStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  const login = useCallback(async ({ email, password }: { email: string; password: string }) => {
    setLoading(true);
    try {
      const data = await gql<{
        AdminLogin: { accessToken: string; refreshToken: string; errors: { code: string; message: string }[] | null };
      }>({
        query: ADMIN_LOGIN_MUTATION,
        variables: { input: { email, password } },
      });
      const { accessToken, refreshToken, errors } = data.AdminLogin;
      if (errors?.length) return { error: errors[0].message };
      if (!accessToken || !refreshToken) {
        return { error: 'Resposta inválida do servidor' };
      }

      // Save tokens BEFORE the viewer query so `gql()` attaches Authorization.
      setToken({ token: accessToken });
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

      // Fetch the admin profile via viewer.admin (adminLogin payload doesn't
      // carry it — keeps the mutation surface narrow).
      const viewerData = await gql<{ viewer: { admin: AuthUser | null } }>({
        query: VIEWER_ADMIN_QUERY,
      });
      const profile = viewerData.viewer.admin;
      if (!profile) {
        // Defensive: tokens were issued but viewer.admin came back null.
        // Wipe local state so we don't trap the user in an unusable session.
        clearToken();
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        return { error: 'Não foi possível carregar o perfil do admin' };
      }

      localStorage.setItem(USER_KEY, JSON.stringify(profile));
      setUser(profile);
      return { error: null };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao fazer login';
      return { error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    // Best-effort server-side invalidation: bump refreshTokenVersion so
    // any refresh tokens already issued become unusable. If the server is
    // unreachable, fall through to local cleanup — the worst case is a
    // stale refresh token that expires in 7d anyway.
    try {
      await gql({ query: ADMIN_LOGOUT_MUTATION });
    } catch {
      // ignored — local clear below still runs
    }
    clearToken();
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
